<template>
  <transition name="cookie-fade">
    <div v-if="showBanner" class="cookie-consent-banner">
      <div class="cookie-container">
        <div class="cookie-content">
          <div class="cookie-close">
            <button class="close-btn" @click="closeBanner">×</button>
          </div>
          
          <div class="cookie-powered-by">
            <div class="ned-logo">
              <img src="/nedcollege/NEDCOLLEGE_logo_blue.svg" alt="NED College" />
            </div>
          </div>
          
          <div class="cookie-header">
            <h3>This website uses cookies</h3>
          </div>
          
          <div class="cookie-message">
            We use cookies to personalise content and ads, to provide social media features and to analyse our traffic. We also share information about your use of our site with our social media, advertising and analytics partners who may combine it with other information that you've provided to them or that they've collected from your use of their services.
          </div>
          
          <div class="cookie-categories">
            <div class="cookie-category">
              <div class="category-label">Necessary</div>
              <div class="toggle-container">
                <div class="toggle-switch">
                  <input type="checkbox" id="necessary" checked disabled>
                  <label for="necessary"></label>
                </div>
              </div>
            </div>
            
            <div class="cookie-category">
              <div class="category-label">Preferences</div>
              <div class="toggle-container">
                <div class="toggle-switch">
                  <input type="checkbox" id="preferences" v-model="preferences">
                  <label for="preferences"></label>
                </div>
              </div>
            </div>
            
            <div class="cookie-category">
              <div class="category-label">Statistics</div>
              <div class="toggle-container">
                <div class="toggle-switch">
                  <input type="checkbox" id="statistics" v-model="statistics">
                  <label for="statistics"></label>
                </div>
              </div>
            </div>
            
            <div class="cookie-category">
              <div class="category-label">Marketing</div>
              <div class="toggle-container">
                <div class="toggle-switch">
                  <input type="checkbox" id="marketing" v-model="marketing">
                  <label for="marketing"></label>
                </div>
              </div>
            </div>
          </div>
          
          <div class="cookie-details">
            <button class="btn btn-link" @click="showDetails = !showDetails">
              Show details
              <i class="arrow" :class="{ 'arrow-down': !showDetails, 'arrow-right': showDetails }"></i>
            </button>
          </div>
          
          <div class="cookie-actions">
            <button class="btn btn-deny" @click="rejectCookies">Deny</button>
            <button class="btn btn-accept-all" @click="acceptCookies">Allow all</button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted } from 'vue';

// Create global consent handler to prevent multiple banners
if (!window.cookieConsentHandled) {
  window.cookieConsentHandled = false;
}

const showBanner = ref(false);
const showDetails = ref(false);
const preferences = ref(false);
const statistics = ref(false);
const marketing = ref(false);

const COOKIE_KEY = 'cookie_consent';
const COOKIE_TIMESTAMP = 'cookie_consent_timestamp';
const COOKIE_PREFERENCES = 'cookie_preferences';
const COOKIE_STATISTICS = 'cookie_statistics';
const COOKIE_MARKETING = 'cookie_marketing';
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Function to create actual browser cookies, not just localStorage
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

// Check if we should show the banner
function checkConsent() {
  // If already handled globally, don't show
  if (window.cookieConsentHandled) {
    showBanner.value = false;
    return;
  }

  const consent = localStorage.getItem(COOKIE_KEY);
  const timestamp = localStorage.getItem(COOKIE_TIMESTAMP);
  const currentTime = new Date().getTime();
  
  // Show banner if:
  // 1. No consent setting exists
  // 2. Consent was rejected AND the session duration has passed
  if (consent === null || 
      (consent === 'rejected' && timestamp && currentTime - parseInt(timestamp) > SESSION_DURATION_MS)) {
    showBanner.value = true;
    
    // Load saved preferences if any
    preferences.value = localStorage.getItem(COOKIE_PREFERENCES) === 'true';
    statistics.value = localStorage.getItem(COOKIE_STATISTICS) === 'true';
    marketing.value = localStorage.getItem(COOKIE_MARKETING) === 'true';
  } else if (consent === 'accepted') {
    // If consent was previously accepted, ensure scripts are loaded
    window.dispatchEvent(new CustomEvent('cookie-consent', { 
      detail: { 
        consent: true,
        preferences: localStorage.getItem(COOKIE_PREFERENCES) === 'true',
        statistics: localStorage.getItem(COOKIE_STATISTICS) === 'true',
        marketing: localStorage.getItem(COOKIE_MARKETING) === 'true'
      } 
    }));
    window.cookieConsentHandled = true;
  } else {
    window.cookieConsentHandled = true;
  }
}

onMounted(() => {
  // Use setTimeout to ensure this runs after all components are mounted
  setTimeout(() => {
    checkConsent();
  }, 100);
});

function savePreferences() {
  localStorage.setItem(COOKIE_PREFERENCES, preferences.value);
  localStorage.setItem(COOKIE_STATISTICS, statistics.value);
  localStorage.setItem(COOKIE_MARKETING, marketing.value);
  
  setCookie('cookie_preferences', preferences.value, 365);
  setCookie('cookie_statistics', statistics.value, 365);
  setCookie('cookie_marketing', marketing.value, 365);
}

function acceptCookies() {
  // Set global flag to prevent other components from showing the banner
  window.cookieConsentHandled = true;
  
  // Set all preferences to true
  preferences.value = true;
  statistics.value = true;
  marketing.value = true;
  
  // Save preferences
  savePreferences();
  
  // Set localStorage
  localStorage.setItem(COOKIE_KEY, 'accepted');
  localStorage.setItem(COOKIE_TIMESTAMP, new Date().getTime().toString());
  
  // Set actual browser cookies
  setCookie('cookie_consent', 'accepted', 365); // 1 year
  setCookie('cookie_consent_timestamp', new Date().getTime().toString(), 365);
  
  showBanner.value = false;
  window.dispatchEvent(new CustomEvent('cookie-consent', { 
    detail: { 
      consent: true,
      preferences: true,
      statistics: true,
      marketing: true
    } 
  }));
}

function rejectCookies() {
  // Set global flag to prevent other components from showing the banner
  window.cookieConsentHandled = true;
  
  // Set all preferences to false except necessary
  preferences.value = false;
  statistics.value = false;
  marketing.value = false;
  
  // Save preferences
  savePreferences();
  
  // Set localStorage
  localStorage.setItem(COOKIE_KEY, 'rejected');
  localStorage.setItem(COOKIE_TIMESTAMP, new Date().getTime().toString());
  
  // Set actual browser cookies
  setCookie('cookie_consent', 'rejected', 1); // 1 day
  setCookie('cookie_consent_timestamp', new Date().getTime().toString(), 1);
  
  showBanner.value = false;
  window.dispatchEvent(new CustomEvent('cookie-consent', { 
    detail: { 
      consent: false,
      preferences: false,
      statistics: false,
      marketing: false
    } 
  }));
}

function closeBanner() {
  // Set global flag to prevent other components from showing the banner
  window.cookieConsentHandled = true;
  
  // Save current preferences
  savePreferences();
  
  // Set localStorage
  localStorage.setItem(COOKIE_KEY, 'customized');
  localStorage.setItem(COOKIE_TIMESTAMP, new Date().getTime().toString());
  
  // Set actual browser cookies
  setCookie('cookie_consent', 'customized', 365);
  setCookie('cookie_consent_timestamp', new Date().getTime().toString(), 365);
  
  showBanner.value = false;
  window.dispatchEvent(new CustomEvent('cookie-consent', { 
    detail: { 
      consent: true,
      preferences: preferences.value,
      statistics: statistics.value,
      marketing: marketing.value
    } 
  }));
}
</script>

<style scoped>
.cookie-consent-banner {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  width: 100%;
}

.cookie-container {
  max-width: 100%;
  width: 100%;
  background: #fff;
  border-radius: 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
}

.cookie-content {
  padding: 1rem 1.5rem;
  background: #fff;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 70px;
}

.cookie-close {
  position: absolute;
  top: 10px;
  right: 10px;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #333;
}

.cookie-powered-by {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-right: 1.5rem;
}

.ned-logo img {
  height: 40px;
  width: auto;
}

.cookie-header {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  margin-right: 1.5rem;
}

.cookie-header h3 {
  margin: 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
}

.cookie-message {
  margin: 0 1rem 0 0;
  color: #555;
  line-height: 1.4;
  font-size: 0.9rem;
  flex: 1;
}

.cookie-categories {
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  margin: 0 1.5rem 0 0;
  padding: 0;
  border: none;
}

.cookie-category {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem;
}

.category-label {
  font-weight: 500;
  font-size: 0.9rem;
  margin-right: 0.75rem;
}

.toggle-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Toggle Switch Styles */
.toggle-switch {
  position: relative;
  width: 50px;
  height: 26px;
  display: inline-block;
  vertical-align: middle;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 34px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch label:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.toggle-switch input:checked + label {
  background-color: #2196F3;
}

.toggle-switch input:checked + label:before {
  transform: translateX(24px);
}

.toggle-switch input:disabled + label {
  opacity: 0.6;
  cursor: not-allowed;
}

.cookie-details {
  margin: 0;
  display: none;
}

.btn-link {
  background: transparent;
  border: none;
  color: #2196F3;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.arrow {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 5px;
  border-top: 2px solid #2196F3;
  border-right: 2px solid #2196F3;
  transition: transform 0.3s;
}

.arrow-down {
  transform: rotate(135deg);
}

.arrow-right {
  transform: rotate(45deg);
}

.cookie-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
  margin: 0;
}

.btn {
  padding: 0.5rem 1.25rem;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-deny {
  background: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.btn-deny:hover {
  background: #f5f5f5;
}

.btn-accept-all {
  background: #2196F3;
  color: white;
}

.btn-accept-all:hover {
  background: #0d8aee;
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .cookie-content {
    flex-direction: column;
    padding: 1.5rem;
    min-height: auto;
  }
  
  .cookie-powered-by {
    margin: 0 0 1rem 0;
  }
  
  .cookie-header {
    max-width: 100%;
    margin-bottom: 0.75rem;
    margin-right: 0;
  }
  
  .cookie-message {
    margin: 0.75rem 0;
    flex: 0 0 100%;
  }
  
  .cookie-categories {
    flex-direction: column;
    margin: 0.75rem 0;
    width: 100%;
    gap: 0.5rem;
  }
  
  .cookie-category {
    width: 100%;
    margin-bottom: 0.5rem;
    padding: 0;
  }
  
  .cookie-actions {
    width: 100%;
    margin-top: 0.75rem;
  }
}

/* Animation */
.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.cookie-fade-enter-from,
.cookie-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style> 